import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";
import { CoreToggle } from "./coreToggle";

const DEFAULT_VERTICAL_QUERY = "(max-width: 1279px)";
export class TktBreakdownController implements Plugin {
  private readonly container: HTMLElement;
  private readonly breakdownSlot: HTMLElement | null;
  private readonly detailsPanel: HTMLElement | null;
  private breakdownContent: HTMLElement | null;
  private mediaQuery: MediaQueryList;
  private isVertical = false;
  private toggle: CoreToggle;

  private readonly mediaChangeHandler: (e: MediaQueryListEvent) => void;

  constructor(private readonly element: HTMLElement) {
    this.container = this.element;

    this.breakdownSlot = this.container.querySelector(
      ".tkt-availabilityPriceContainer__breakdown-slot",
    );

    const detailsId = this.container.dataset.breakdownDetailsId;
    this.detailsPanel = detailsId
      ? this.container.querySelector(`#${detailsId}`)
      : null;

    const toggleButton: HTMLButtonElement | null = detailsId
      ? this.container.querySelector(`button[aria-controls="${detailsId}"]`)
      : null;

    this.breakdownContent = this.breakdownSlot
      ?.firstElementChild as HTMLElement | null;

    const verticalQuery =
      this.container.dataset.breakdownVerticalQuery || DEFAULT_VERTICAL_QUERY;
    this.mediaQuery = window.matchMedia(verticalQuery);

    this.toggle = new CoreToggle(this.container, {
      trigger: toggleButton,
      target: this.breakdownSlot,
    });

    this.mediaChangeHandler = (e) => this.handleMediaChange(e);
  }

  init(): void {
    if (!this.breakdownContent || !this.toggle.getTrigger()) return;

    this.toggle.init();
    this.mediaQuery.addEventListener("change", this.mediaChangeHandler);

    this.isVertical = this.mediaQuery.matches;
    this.moveBreakdown();
    this.toggle.close();
  }

  destroy(): void {
    this.mediaQuery.removeEventListener("change", this.mediaChangeHandler);
    this.toggle.destroy();
  }

  private handleMediaChange(e: MediaQueryListEvent): void {
    if (e.matches === this.isVertical) return;

    this.isVertical = e.matches;
    this.moveBreakdown();
    this.toggle.close();
  }

  private moveBreakdown(): void {
    if (!this.breakdownContent) return;

    if (this.isVertical) {
      const inner =
        this.detailsPanel?.querySelector(
          ".core-buttonOpenDetails__details-inner",
        ) ?? this.detailsPanel;
      inner?.appendChild(this.breakdownContent);
      this.breakdownSlot?.setAttribute("aria-hidden", "true");
      this.toggle.setTarget(this.detailsPanel);
    } else {
      this.breakdownSlot?.appendChild(this.breakdownContent);
      this.detailsPanel?.setAttribute("aria-hidden", "true");
      this.toggle.setTarget(this.breakdownSlot);
    }
  }
}

PluginRegistry.register("tktBreakdownController", TktBreakdownController);
